import * as React from 'react';
import { observer } from 'mobx-react';
import { EligibilityRuleTabView } from 'nomst/Stores/Budget/eligibilityRuleTabView';
import { EligibilityRule } from 'nomst/Stores/Configuration/eligibilityRule';
import { EligibilityRuleCard } from 'nomst/Components/EligibilityProration/eligibilityRuleCard';
import { DeleteEligibilityRuleModal } from 'nomst/Components/EligibilityProration/deleteEligibilityRuleModal';
import Loading from 'components/Common/loading';
import './eligibilityRules.scss';

interface IEligibilityRulesProps {
    eligibilityRuleTabView: EligibilityRuleTabView;
}

interface IEligibilityRulesState {
    showDeleteModal: boolean;
    ruleToDelete: Nullable<EligibilityRule>;
}

@observer
export class EligibilityRules extends React.Component<IEligibilityRulesProps, IEligibilityRulesState> {
    constructor(props: IEligibilityRulesProps) {
        super(props);

        this.handleAddRuleClick = this.handleAddRuleClick.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleDeleteModalClose = this.handleDeleteModalClose.bind(this);
        this.renderRule = this.renderRule.bind(this);
        this.handleDeleteRule = this.handleDeleteRule.bind(this);

        this.state = {
            showDeleteModal: false,
            ruleToDelete: null
        };
    }

    async componentDidMount(): Promise<void> {
        const { eligibilityRuleTabView } = this.props;
        await Promise.all([
            eligibilityRuleTabView.fetchRules(),
            eligibilityRuleTabView.fetchDepartments(),
            eligibilityRuleTabView.fetchWorkLocations()
        ]);
    }

    render(): React.ReactNode {
        const { eligibilityRuleTabView } = this.props;
        const { showDeleteModal } = this.state;
        if (eligibilityRuleTabView.isLoading) {
            return (
                <Loading className="nomst-eligibility-rules nomst-eligibility-rules--loading" />
            );
        }

        if (!eligibilityRuleTabView.doesHaveBudget) {
            return this.renderNoBudget();
        }

        if (!eligibilityRuleTabView.eligibilityRules.length) {
            return this.renderNoRules();
        }

        return (
            <div className="nomst-eligibility-rules">
                {eligibilityRuleTabView.eligibilityRules.map(this.renderRule)}
                {!eligibilityRuleTabView.anyRulesInEditMode &&
                <button
                    className="pxl-link nomst-eligibility-rules__add-rule"
                    onClick={this.handleAddRuleClick}
                >
                    <i className="icon-border icon-circle-thin">
                        <i className="icon-plus" />
                    </i>
                    <span>Add Eligibility Rule</span>
                </button>}

                {showDeleteModal && (
                    <DeleteEligibilityRuleModal
                        onClose={this.handleDeleteModalClose}
                        onConfirm={this.handleDeleteRule}
                        isDeleting={eligibilityRuleTabView.isLoading}
                    />
                )}
            </div>
        );
    }

    private renderRule(rule: EligibilityRule, index: number): React.ReactNode {
        const { removeRule, updateAssociatedAmounts, departments, workLocations } = this.props.eligibilityRuleTabView;
        return (
            <EligibilityRuleCard
                key={index}
                rule={rule}
                departments={departments}
                workLocations={workLocations}
                removeRule={removeRule}
                handleDeleteClick={this.handleDeleteClick}
                updateOtherRuleAmounts={updateAssociatedAmounts}
            />
        );
    }

    private renderNoBudget(): React.ReactNode {
        return (
            <div className="nomst-eligibility-rules nomst-eligibility-rules--no-budget">
                <img
                    alt=""
                    src={require('contents/eligibility_no_budget.svg')}
                    className="nomst-eligibility-rules__no-budget-image"
                />
                <p>In order to add eligibility rules, please create budget first</p>
            </div>
        );
    }

    private renderNoRules(): React.ReactNode {
        return (
            <div className="nomst-eligibility-rules nomst-eligibility-rules--no-rules">
                <p>
                    Eligibility rules allow you to automatically mark employees’ eligibility for a budget
                    based on the period of time they have been with your org or based on their last pay change.
                </p>
                <button
                    className="pxl-btn pxl-btn--primary nomst-eligibility-rules__add-btn"
                    onClick={this.handleAddRuleClick}
                >
                    Add Eligibility Rule
                </button>
            </div>
        );
    }

    private handleAddRuleClick(): void {
        this.props.eligibilityRuleTabView.addNewRule();
    }

    private handleDeleteClick(rule: EligibilityRule): void {
        this.setState({
            showDeleteModal: true,
            ruleToDelete: rule
        });
    }

    private handleDeleteModalClose(): void {
        this.setState({
            showDeleteModal: false,
            ruleToDelete: null
        });
    }

    private async handleDeleteRule(): Promise<void> {
        const { ruleToDelete } = this.state;
        if (ruleToDelete) {
            await this.props.eligibilityRuleTabView.deleteRule(ruleToDelete);
            this.setState({
                showDeleteModal: false,
                ruleToDelete: null
            });
        }
    }
}

export default { EligibilityRules };